import React, { createContext, forwardRef, useContext, useMemo } from 'react';

import { Field } from '../Field';
import { checkboxGroupItems } from './CheckboxGroup.css';

export interface CheckboxGroupContextValue {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}

const CheckboxGroupContext = createContext<CheckboxGroupContextValue | undefined>(undefined);

export const useCheckboxGroup = () => {
  return useContext(CheckboxGroupContext);
};

export interface CheckboxGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  label?: React.ReactNode;
  value?: string[];
  onChange?: (value: string[]) => void;
  direction?: 'vertical' | 'horizontal';
  gap?: number;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  children?: React.ReactNode;
}

export const CheckboxGroup = forwardRef<HTMLDivElement, CheckboxGroupProps>(
  (
    {
      label,
      value = [],
      onChange,
      direction = 'vertical',
      gap = 12,
      disabled = false,
      error,
      required = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const contextValue = useMemo(
      () => ({
        value,
        onChange,
        disabled,
      }),
      [value, onChange, disabled],
    );

    return (
      <CheckboxGroupContext.Provider value={contextValue}>
        <Field
          ref={ref}
          role="group"
          label={typeof label === 'string' ? label : String(label ?? '')}
          required={required}
          error={error}
          className={className}
          {...props}
        >
          <div className={checkboxGroupItems({ direction })} style={{ gap: `${gap}px` }}>
            {children}
          </div>
        </Field>
      </CheckboxGroupContext.Provider>
    );
  },
);

CheckboxGroup.displayName = 'CheckboxGroup';
